import React from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import ServicePageShell, {
  RelatedServices,
} from "../components/ServicePageShell";
import ServiceInquiryForm from "../components/ServiceInquiryForm";
import siteData from "../data/siteData";
import { createBreadcrumbList, createContactPageSchema } from "../utils/seo";

const relatedLinks = [
  {
    to: "/",
    kicker: "Home",
    title: "Back to QBITS9",
    description: "Return to the homepage and explore the company overview and service summary.",
    cta: "Open home",
  },
  {
    to: "/services/ai-automation",
    kicker: "Service",
    title: "AI automation",
    description: "Reduce repetitive work and build smarter workflows for your team.",
    cta: "View page",
  },
  {
    to: "/services/web-mobile-development",
    kicker: "Service",
    title: "Web & mobile apps",
    description: "Build scalable product experiences for web, mobile, and API layers.",
    cta: "View page",
  },
  {
    to: "/services/saas-erp-development",
    kicker: "Service",
    title: "SaaS & ERP",
    description: "Create secure platforms and internal systems that fit your operating model.",
    cta: "View page",
  },
  {
    to: "/services/desktop-software",
    kicker: "Service",
    title: "Desktop software",
    description: "Build reliable desktop apps, offline tools, and internal business software.",
    cta: "View page",
  },
];

const heroStats = [
  {
    title: "Based in Surat",
    value: siteData.addressDisplay,
  },
  {
    title: "Fast response",
    value: "We usually reply after a quick review of your brief.",
  },
  {
    title: "Practical guidance",
    value: "Expect a conversation focused on business outcomes.",
  },
];

const Contact = () => {
  const schemas = [
    createContactPageSchema({ path: "/contact" }),
    createBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Contact", path: "/contact" },
    ]),
  ];

  return (
    <ServicePageShell
      title="Contact QBITS9 | AI Automation and Digital Solutions"
      description="Contact QBITS9 in Surat, India to discuss AI automation, web and mobile apps, SaaS and ERP systems, or desktop software."
      canonicalPath="/contact"
      keywords="contact QBITS9, AI automation company Surat, web app development India, SaaS ERP development, desktop software development"
      schemas={schemas}
    >
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-24 lg:px-16">
          <nav className="flex flex-wrap items-center gap-2 lg:text-base text-sm font-[500] text-blue font-poppins lg:w-[65%] w-full px-5 lg:px-0">
            <Link to="/" className="hover:text-[#FF6B00]">
              Home
            </Link>
            <span>/</span>
            <span className="text-slate-900">Contact</span>
          </nav>

          <div className=" grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] lg:items-center">
            <div className="space-y-8">
              <div className="space-y-5">
                <span className="font-peachi text-[16px] md:text-[18px] text-black font-[500] mb-3">
                  Surat, India
                  <span className="text-primary p-[8px] bg-[#ffbf7e1f] rounded-[30px] ml-2">
                    Let&apos;s plan your next build
                  </span>
                </span>
                <h1 className="text-black xl:text-[58px] lg:text-[50px] md:text-[42px] text-[28px] font-bold leading-[1.3]">
                  Talk to <span className="text-primary">QBITS9</span>
                </h1>
                <p className="max-w-2xl text-lg leading-8 text-slate-600 font-poppins">
                  Share a short brief and we will help you decide whether the right move is AI automation, a custom app, a platform build, or better growth infrastructure.
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <HashLink
                  to="#service-inquiry"
                  smooth
                  className="w-fit relative overflow-hidden flex items-center justify-center bg-primary px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group cursor-pointer"
                >
                  <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">
                    Start a Conversation
                  </span>
                  <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E85D00] transition-all duration-500 group-hover:h-[25rem] group-hover:w-[25rem]" />
                </HashLink>
                <a
                  href={`tel:${siteData.phoneHref}`}
                  className="w-fit relative overflow-hidden flex items-center justify-center bg-blue px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group"
                >
                  <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">
                    Call {siteData.phoneDisplay}
                  </span>
                  <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700 group-hover:w-[25rem] group-hover:h-[25rem] -translate-x-1/2 -translate-y-1/2" />
                </a>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                {heroStats.map((item) => (
                  <article
                    key={item.title}
                    className="h-full bg-white rounded-[22px] py-[18px] px-[18px] flex flex-col gap-2 shadow-[0_12px_30px_0_#0000000F] hover:shadow-[0_12px_30px_0_#00000030] transition duration-500 ease-in-out cursor-pointer"
                  >
                    <p className="text-[18px] font-bold text-primary">
                      {item.title}
                    </p>
                    <p className="text-[15px] font-light text-lightBlack line-clamp-3">
                      {item.value}
                    </p>
                  </article>
                ))}
              </div>
            </div>

            <div className="lg:pl-4">
              <div id="service-inquiry">
                <ServiceInquiryForm
                  title="Send us your enquiry"
                  description="We will review your brief and suggest the most relevant next step."
                  submitLabel="Send Message"
                  serviceName="General Contact"
                  defaultSource="Contact page"
                  sourceOptions={[
                    "Contact page",
                    "Referral",
                    "Search",
                    "Social media",
                    "Other",
                  ]}
                  successTitle="Thanks for contacting QBITS9"
                  successDescription="Your message is in. We will review it and reply as soon as possible."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedServices
        links={relatedLinks}
        title="Continue exploring"
        description="If you are still comparing options, jump to one of the service pages or go back home."
        variant="service"
      />
    </ServicePageShell>
  );
};

export default Contact;
